import { Ref } from 'vue';
import { useRoute } from 'vue-router';

import { HgBreadcrumbModel, HgSupportedLanguage } from '@smg-real-estate/domus';
import { Listing } from '@smg-real-estate/hg-rets-schema';
import { ListingType } from '@smg-real-estate/hg-search-libs';
import { TreatmentConfig } from '@smg-real-estate/vue-experiments';

import {
  trackBottomSheetDisplayed,
  trackBreadCrumbClicked,
  trackDetailPageVirtualPageView,
  trackFraudReportAction,
  trackFraudReportSubmit,
  trackListingAccessoryClick,
  trackOpenPrint,
  trackOutboundLink,
  trackPartnerWidget,
  trackShareAction,
  trackShareOpen,
} from './DetailPage.tracking';

import type { ServiceBoxConfig } from '@/components/ServiceBoxes/helpers/serviceboxes-helpers';
import { InteractionContainerName, InteractionGoal } from '@/helpers/GTM/GTMParameters';
import type { NewConstructionDataWithOtherListings } from '@/models';
import type { HgImage } from '@/store/types';

export interface UseDetailPageTrackingParams {
  listing: Ref<Listing | undefined>;
  listingType: Ref<ListingType | undefined>;
  currentLocale: Ref<HgSupportedLanguage>;
  listingImages: Ref<HgImage[] | undefined>;
  newConstructionProject: Ref<NewConstructionDataWithOtherListings | null>;
  dataFetchError: Ref<{ message: string; listingId: string; response: any } | undefined>;
  sessionId: Ref<string | undefined>;
  normalizedFullPath: Ref<string>;
  treatments: Ref<Record<string, TreatmentConfig>>;
  serviceBoxes: Ref<ServiceBoxConfig[]>;
  isLayoutSolutionEnabled: Ref<boolean>;
  listerBranding: Ref<ListerBranding | undefined>;
  shouldShowAgencyCard: Ref<boolean>;
}

export function useDetailPageTracking(params: UseDetailPageTrackingParams) {
  const route = useRoute();

  const trackVirtualPageView = async (
    loginStatusTrackingParameters: Record<string, string | undefined>,
  ): Promise<void> => {
    if (params.listing.value) {
      trackDetailPageVirtualPageView(
        params.currentLocale.value,
        params.listing.value,
        params.listingType.value,
        params.listingImages.value?.length ?? 0,
        params.newConstructionProject.value,
        loginStatusTrackingParameters,
        params.treatments.value,
        params.isLayoutSolutionEnabled.value ? [] : params.serviceBoxes.value,
        params.listerBranding.value?.subscriptionType,
        params.shouldShowAgencyCard.value,
      );
    }
  };

  const trackFraudReportActionEvent = (): void => {
    trackFraudReportAction(InteractionContainerName.REPORT_BOX);
  };

  const trackShareActionEvent = (channel: InteractionGoal): void => {
    trackShareAction(params.listing.value?.id as string, params.sessionId.value ?? '', channel);
  };

  const trackBreadCrumbClick = (breadCrumbModel: HgBreadcrumbModel): void => {
    if (breadCrumbModel.trackingMeta) {
      trackBreadCrumbClicked(breadCrumbModel.trackingMeta);
    }
  };

  return {
    trackVirtualPageView,
    trackPartnerWidget,
    trackOpenPrint,
    trackShareOpen,
    trackShareActionEvent,
    trackFraudReportActionEvent,
    trackFraudReportSubmit,
    trackListingAccessoryClick,
    trackOutboundLink,
    trackBottomSheetDisplay: trackBottomSheetDisplayed,
    trackBreadCrumbClick,
  };
}
